import React, { useState, useEffect, useCallback } from "react";
import { authFetch, API_BASE } from "../api";
import "./shared/shared.css";
import "./PlayerPage.css";

export default function PlayerPage({ cmsAuth }) {
  const [localInfo, setLocalInfo] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [localVersion, setLocalVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(null); // env_tenant key or "all"
  const [showDeploy, setShowDeploy] = useState(false);
  const [deployTenant, setDeployTenant] = useState("");
  const [deployRootGroupId, setDeployRootGroupId] = useState("");
  const [workspaceOptions, setWorkspaceOptions] = useState([]); // depth-0 workspaces for selected tenant
  const [workspaceInfo, setWorkspaceInfo] = useState(null); // { tracked, wouldPick } or null
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [workspacesError, setWorkspacesError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [vRes, dRes] = await Promise.all([
        authFetch(`${API_BASE}/apk/player/version`),
        authFetch(`${API_BASE}/apk/player/deployments`),
      ]);
      const vData = await vRes.json();
      const dData = await dRes.json();
      setLocalInfo(vData.available ? vData : null);
      setDeployments(dData.deployments || []);
      setLocalVersion(dData.local || null);
    } catch (err) {
      console.error("Failed to fetch player data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // When the deploy panel opens (or its env/tenant change), reset and fetch
  // the list of top-level workspaces for that tenant so the user can pick one.
  useEffect(() => {
    if (!showDeploy || !cmsAuth) {
      setWorkspaceOptions([]);
      setWorkspaceInfo(null);
      setWorkspacesError(null);
      return;
    }
    const effectiveTenant = deployTenant || cmsAuth.tenant;
    if (!effectiveTenant) return;
    let cancelled = false;
    setWorkspacesLoading(true);
    setWorkspacesError(null);
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/apk/player/workspaces/${encodeURIComponent(effectiveTenant)}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(typeof data.error === "string" ? data.error : "Failed to load workspaces");
        setWorkspaceOptions(data.depth0 || []);
        setWorkspaceInfo({ tracked: data.tracked, wouldPick: data.wouldPick });
        // Pre-select what the backend *would* pick by default, so the user
        // sees the same thing they'd get with no manual change.
        setDeployRootGroupId(data.wouldPick?.id || "");
      } catch (err) {
        if (!cancelled) setWorkspacesError(err.message);
      } finally {
        if (!cancelled) setWorkspacesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showDeploy, cmsAuth, deployTenant]);

  const deployToTenant = async () => {
    setDeploying("deploy");
    // Match the <select value={...}> precedence exactly: if the user never
    // manually changed the tenant dropdown, deployTenant is still the empty
    // string — but the dropdown visually shows cmsAuth.tenant. We need to
    // send what the user sees, not what state happens to hold.
    const effectiveTenant = deployTenant || cmsAuth?.tenant || undefined;
    try {
      const res = await authFetch(`${API_BASE}/apk/player/deploy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant: effectiveTenant,
          rootGroupId: deployRootGroupId || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.error === 'string' ? data.error : data.error?.message || "Request failed");
      setShowDeploy(false);
      setDeployTenant("");
      setDeployRootGroupId("");
      fetchData();
    } catch (err) {
      alert("Deploy failed: " + err.message);
    } finally {
      setDeploying(null);
    }
  };

  const updateAll = async () => {
    if (!window.confirm("Update Player APK on all tracked tenants?")) return;
    setDeploying("all");
    try {
      const res = await authFetch(`${API_BASE}/apk/player/update-all`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.error === 'string' ? data.error : data.error?.message || "Request failed");
      const updated = data.results?.filter((r) => r.status === "updated").length || 0;
      const failed = data.results?.filter((r) => r.status === "failed").length || 0;
      alert(`Updated: ${updated}, Failed: ${failed}, Up-to-date: ${data.results?.filter((r) => r.status === "up-to-date").length || 0}`);
      fetchData();
    } catch (err) {
      alert("Update all failed: " + err.message);
    } finally {
      setDeploying(null);
    }
  };

  const removeTracking = async (env, tenant) => {
    if (!window.confirm(`Remove tracking for ${tenant} (${env})?`)) return;
    await authFetch(`${API_BASE}/apk/player/deployments/${env}/${tenant}`, { method: "DELETE" });
    fetchData();
  };

  const outdatedCount = deployments.filter((d) => d.outdated).length;

  return (
    <div className="player-page">
      <div className="page-header">
        <h2>Forge Studio Player</h2>
        <div className="page-header-right">
          {localInfo && (
            <span className="player-version-badge">v{localInfo.version}</span>
          )}
          <span className="apps-stat">{deployments.length} deployments</span>
          {outdatedCount > 0 && (
            <span className="apps-stat" style={{ color: "#fbbf24" }}>{outdatedCount} outdated</span>
          )}
        </div>
      </div>

      <div className="page-toolbar">
        <div className="page-toolbar-left">
          <button
            className="player-action-btn player-deploy-btn"
            onClick={() => setShowDeploy(!showDeploy)}
          >
            + Deploy to Tenant
          </button>
          {outdatedCount > 0 && (
            <button
              className="player-action-btn player-update-btn"
              onClick={updateAll}
              disabled={deploying === "all"}
            >
              {deploying === "all" ? "Updating..." : `Update All (${outdatedCount})`}
            </button>
          )}
        </div>
      </div>

      {/* Deploy panel */}
      {showDeploy && (
        <div className="player-deploy-panel">
          {!cmsAuth ? (
            <div className="player-deploy-row" style={{ color: "var(--color-text-muted)", fontSize: 13 }}>
              Sign in to a Videri tenant to deploy.
            </div>
          ) : (
            <div className="player-deploy-row">
              <label className="player-deploy-label">
                <span>Tenant</span>
                <select
                  value={deployTenant || cmsAuth.tenant || ""}
                  onChange={(e) => {
                    setDeployTenant(e.target.value);
                    setDeployRootGroupId(""); // reset workspace when tenant changes
                  }}
                  className="player-deploy-input"
                >
                  {(cmsAuth.tenants && cmsAuth.tenants.length > 0 ? cmsAuth.tenants : [cmsAuth.tenant].filter(Boolean)).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
              <label className="player-deploy-label">
                <span>Workspace {workspacesLoading ? "(loading…)" : workspaceInfo?.tracked?.stillVisible ? "(tracked)" : workspaceInfo?.tracked?.rootGroupId && !workspaceInfo.tracked.stillVisible ? "(tracked one missing — pick another)" : "(first-time deploy)"}</span>
                <select
                  value={deployRootGroupId}
                  onChange={(e) => setDeployRootGroupId(e.target.value)}
                  className="player-deploy-input"
                  disabled={workspacesLoading || !!workspacesError}
                >
                  {workspaceOptions.length === 0 && <option value="">{workspacesError ? "Error loading" : "No workspaces"}</option>}
                  {workspaceOptions.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name} ({w.id.slice(0, 8)}…)
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="player-action-btn player-deploy-btn"
                onClick={deployToTenant}
                disabled={!!deploying || workspacesLoading || !deployRootGroupId || !cmsAuth?.tenant}
                style={{ alignSelf: "flex-end" }}
              >
                {deploying === "deploy" ? "Deploying..." : "Upload & Share"}
              </button>
            </div>
          )}
          {cmsAuth && workspacesError && (
            <div className="player-deploy-row" style={{ color: "#ef4444", fontSize: 12, marginTop: 8 }}>
              Could not load workspaces: {workspacesError}
            </div>
          )}
          {cmsAuth && workspaceInfo?.tracked && !workspaceInfo.tracked.stillVisible && (
            <div className="player-deploy-row" style={{ color: "#fbbf24", fontSize: 12, marginTop: 8 }}>
              ⚠️ Previously-tracked workspace <code>{workspaceInfo.tracked.rootGroupName || workspaceInfo.tracked.rootGroupId}</code> is no longer visible for this tenant — pick a new one.
            </div>
          )}
          {cmsAuth && workspaceInfo?.wouldPick && !workspaceInfo.tracked && workspaceOptions.length > 1 && (
            <div className="player-deploy-row" style={{ color: "var(--color-text-muted)", fontSize: 12, marginTop: 8 }}>
              {workspaceOptions.length} workspaces available — default is <code>{workspaceInfo.wouldPick.name}</code> ({workspaceInfo.wouldPick.reason}). Pick a different one above if this isn't right.
            </div>
          )}
        </div>
      )}

      <div className="page-body">
        {/* Local APK info */}
        {localInfo && (
          <div className="player-local-card">
            <div className="player-local-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L20 7V17L12 22L4 17V7L12 2Z" />
                <path d="M12 12L20 7M12 12V22M12 12L4 7" />
              </svg>
              <div>
                <div className="player-local-title">Local Player APK</div>
                <div className="player-local-meta">
                  v{localInfo.version} (code {localInfo.versionCode}) &middot; {localInfo.sizeMB} MB &middot; Built {new Date(localInfo.buildDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        )}
        {!localInfo && !loading && (
          <div className="page-empty">No local Player APK found. Build or upload one first.</div>
        )}

        {/* Deployments table */}
        {loading ? (
          <div className="page-empty">Loading deployments...</div>
        ) : deployments.length === 0 ? (
          <div className="page-empty">
            No deployments tracked yet. Click "Deploy to Tenant" to upload the Player to a CMS environment, or it will be auto-uploaded when you first publish an app as APK.
          </div>
        ) : (
          <table className="data-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Root Workspace</th>
                <th>Player Version</th>
                <th>Status</th>
                <th>Deployed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deployments.map((dep) => {
                const key = `${dep.env}_${dep.tenant}`;
                return (
                  <tr key={key}>
                    <td data-label="Tenant" style={{ fontWeight: 500 }}>{dep.tenant}</td>
                    <td data-label="Root Workspace" style={{ color: "var(--color-text-muted)", fontSize: 12 }}>{dep.rootGroupName || dep.rootGroupId}</td>
                    <td data-label="Player Version">v{dep.version}</td>
                    <td data-label="Status">
                      {dep.outdated ? (
                        <span className="player-status outdated">Outdated</span>
                      ) : (
                        <span className="player-status current">Up to date</span>
                      )}
                    </td>
                    <td data-label="Deployed" style={{ color: "var(--color-text-faint)", fontSize: 12 }}>
                      {dep.uploadedAt ? new Date(dep.uploadedAt).toLocaleDateString() : "—"}
                    </td>
                    <td data-label="Actions">
                      <div className="td-actions">
                        {dep.outdated && (
                          <button
                            className="apps-btn apps-btn-publish"
                            onClick={async () => {
                              setDeploying(key);
                              try {
                                const res = await authFetch(`${API_BASE}/apk/player/deploy`, {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ tenant: dep.tenant }),
                                });
                                if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
                                fetchData();
                              } catch (err) { alert(err.message); }
                              finally { setDeploying(null); }
                            }}
                            disabled={deploying === key}
                          >
                            {deploying === key ? "Updating..." : "Update"}
                          </button>
                        )}
                        <button className="apps-btn apps-btn-danger" onClick={() => removeTracking(dep.env, dep.tenant)}>
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
